/**
 * Syntax colours for code, as runs of text with a colour each: Prism's
 * grammars, read as tokens rather than as the HTML Prism would write, so a
 * reply's code block and the composer's draw the same colours and neither
 * sets HTML.
 *
 * The colours are for `--surface-code`, which is dark in both themes. A
 * language this does not know, or none, is one run with no colour.
 */

import './prismManual';
import Prism from 'prismjs';
// After the languages each extends. prismjs itself brings markup, CSS,
// C-like and JavaScript.
import 'prismjs/components/prism-markup-templating';
import 'prismjs/components/prism-c';
import 'prismjs/components/prism-cpp';
import 'prismjs/components/prism-csharp';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-kotlin';
import 'prismjs/components/prism-go';
import 'prismjs/components/prism-rust';
import 'prismjs/components/prism-swift';
import 'prismjs/components/prism-php';
import 'prismjs/components/prism-ruby';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-lua';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-powershell';
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-yaml';
import 'prismjs/components/prism-toml';
import 'prismjs/components/prism-ini';
import 'prismjs/components/prism-markdown';
import 'prismjs/components/prism-diff';
import 'prismjs/components/prism-docker';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-tsx';

export interface Run {
  text: string;
  /** A CSS colour; null for the block's own. */
  colour: string | null;
}

/** Names people and models write that Prism knows by another. */
const ALIASES: Record<string, string> = {
  py: 'python',
  python3: 'python',
  js: 'javascript',
  mjs: 'javascript',
  cjs: 'javascript',
  node: 'javascript',
  ts: 'typescript',
  sh: 'bash',
  shell: 'bash',
  zsh: 'bash',
  ps1: 'powershell',
  pwsh: 'powershell',
  'c++': 'cpp',
  cc: 'cpp',
  cxx: 'cpp',
  hpp: 'cpp',
  h: 'c',
  cs: 'csharp',
  'c#': 'csharp',
  kt: 'kotlin',
  rs: 'rust',
  rb: 'ruby',
  golang: 'go',
  yml: 'yaml',
  md: 'markdown',
  html: 'markup',
  xml: 'markup',
  svg: 'markup',
  dockerfile: 'docker',
  patch: 'diff',
  jsonc: 'json',
  json5: 'json',
  postgres: 'sql',
  postgresql: 'sql',
  mysql: 'sql',
  sqlite: 'sql'
};

/** By Prism token type, innermost first when a token has several. */
const COLOURS: Record<string, string> = {
  comment: 'var(--slate-500)',
  prolog: 'var(--slate-500)',
  doctype: 'var(--slate-500)',
  cdata: 'var(--slate-500)',
  keyword: 'var(--violet-400)',
  atrule: 'var(--violet-400)',
  important: 'var(--violet-400)',
  tag: 'var(--violet-400)',
  selector: 'var(--violet-400)',
  string: 'var(--teal-300)',
  char: 'var(--teal-300)',
  'attr-value': 'var(--teal-300)',
  regex: 'var(--teal-300)',
  url: 'var(--teal-300)',
  inserted: 'var(--teal-300)',
  number: 'var(--warning-500)',
  boolean: 'var(--warning-500)',
  constant: 'var(--warning-500)',
  symbol: 'var(--warning-500)',
  decorator: 'var(--warning-500)',
  annotation: 'var(--warning-500)',
  function: 'var(--indigo-300)',
  property: 'var(--indigo-200)',
  'class-name': 'var(--mauve-300)',
  builtin: 'var(--mauve-300)',
  'attr-name': 'var(--mauve-300)',
  namespace: 'var(--mauve-300)',
  deleted: 'var(--danger-500)',
  operator: 'var(--slate-400)',
  punctuation: 'var(--slate-400)'
};

/** Prism's grammar for a fence's language, or null. */
export function grammarFor(lang: string): Prism.Grammar | null {
  const name = lang.trim().toLowerCase();
  const key = ALIASES[name] ?? name;
  if (!key || !Object.prototype.hasOwnProperty.call(Prism.languages, key)) return null;
  // `languages` also holds Prism's own helpers, which are functions.
  const grammar = (Prism.languages as Record<string, unknown>)[key];
  return grammar && typeof grammar === 'object' ? (grammar as Prism.Grammar) : null;
}

function colourOf(types: readonly string[]): string | null {
  for (let i = types.length - 1; i >= 0; i--) {
    const colour = COLOURS[types[i]!];
    if (colour) return colour;
  }
  return null;
}

function flatten(stream: Prism.TokenStream, types: readonly string[], out: Run[]): void {
  if (typeof stream === 'string') {
    if (!stream) return;
    const colour = colourOf(types);
    const last = out[out.length - 1];
    if (last && last.colour === colour) last.text += stream;
    else out.push({ text: stream, colour });
    return;
  }
  if (Array.isArray(stream)) {
    for (const part of stream) flatten(part, types, out);
    return;
  }
  const alias = stream.alias ? (Array.isArray(stream.alias) ? stream.alias : [stream.alias]) : [];
  flatten(stream.content, [...types, stream.type, ...alias], out);
}

/** The code as coloured runs. Their texts joined are the code, exactly. */
export function highlight(code: string, lang: string): Run[] {
  const grammar = grammarFor(lang);
  if (!grammar || !code) return code ? [{ text: code, colour: null }] : [];
  const out: Run[] = [];
  flatten(Prism.tokenize(code, grammar), [], out);
  return out;
}
